import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Divider, Notification, Spinner, StackView, Typography } from '@telus-uds/components-web';
import queryString from 'query-string';

import request, { apiHost } from '../../utils/request';
import FieldCheckbox from '../../form/fields/fieldCheckbox';
import ValidateLoader from '../../UI/validateLoader';
import Loading from '../../signContract/loading';

import { ContentWrapper, SignatureContainer } from '../../design/css';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';

const SignBusinessAppContract = ({ location: { search } }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    contractSignature: {
      headerBA,
      consetText,
      errorMessage,
      errorCheck,
      acceptButton,
      loadingMessage,
      submittingMessage,
      signatureText,
      clearText
    }
  } = localizedStrings;
  const history = useHistory();
  const [orderData, setOrderData] = useState();
  const [signature, setSignature] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const signCanvas = useRef();
  const { token, lang } = queryString.parse(search);

  const getContractUrl = () => {
    const { id } = orderData;
    return `${apiHost}/orders/${id}/business-app-contract?lang=${lang}`;
  };

  useEffect(() => {
    const checkToken = async () => {
      const { data } = await request.get(
        `/orders/validate-token/?token=${token}`
      );

      if (data.success) {
        setOrderData(data);
      } else {
        history.push('/sign-ba-contract/expired');
      }
    };
    checkToken();
  }, []);

  const captureSignature = () => {
    if (signCanvas.current.isEmpty()) {
      setError(errorMessage);
      return;
    }
    if (!isChecked) {
      setError(errorCheck);
      return;
    }

    const signContract = async () => {
      setSubmitting(true);
      const payload = { token, lang, signature };
      const res = await request.post('/orders/contract-signed', payload);
      setSubmitting(false);
      if (res.data.success) {
        history.push('/sign-ba-contract/success');
      } else {
        history.push('/sign-ba-contract/expired');
      }
    };

    signContract();
  };

  const clearSignature = () => {
    if (!signCanvas.current.isEmpty()) {
      signCanvas.current.clear();
      setSignature(null);
    }
  };

  const onChangeOfSignaturePad = () => {
    setError(false);
    setSignature(signCanvas.current.getTrimmedCanvas().toDataURL());
  };

  const onChange = () => setIsChecked(!isChecked);

  const checkBoxFieldData = {
    checked: isChecked,
    name: 'consent',
    value: 'consent',
    label: consetText,
    onChange
  };

  return !orderData ? (
    <Loading isVisible>
      <ValidateLoader message={loadingMessage} />
    </Loading>
  ) : (
    <>
      <div className="container 85%">
        <Spinner
          fullScreen={submitting}
          label={submittingMessage}
          show={submitting}
        />
        <ContentWrapper>
          <Typography heading="h2" variant={{ size: 'h2' }} block>{headerBA}</Typography>
          <Divider space={2} variant={{ inverse: true }} />
          <embed
            src={getContractUrl()}
            type="application/pdf"
            width="100%"
            height="600px"
          />
          <Divider space={2} variant={{ inverse: true }} />
          <StackView space={3}>
            <StackView space={1}>
              <FieldCheckbox data={checkBoxFieldData} />
            </StackView>
            <Typography heading="h4" variant={{ size: 'h4' }} block>{signatureText}</Typography>
            <SignatureContainer>
              <SignatureCanvas
                ref={signCanvas}
                onEnd={onChangeOfSignaturePad}
                penColor="green"
                canvasProps={{
                  width: 400,
                  height: 150,
                  className: 'sigCanvas'
                }}
              />
              {error && (
                <Notification variant={{ validation: 'error' }}>
                  {error}
                </Notification>
              )}
            </SignatureContainer>
            {signature && (
              <div>
                <Button variant={{ text: true }} onPress={clearSignature}>{clearText}</Button>
              </div>
            )}
            <div>
              <Button onPress={captureSignature}>{acceptButton}</Button>
              </div>
            </StackView>
          </ContentWrapper>
        </div>
      </>
    );
  };

SignBusinessAppContract.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

export default SignBusinessAppContract;
